import { Injectable } from '@angular/core';
import { Observable, of, delay } from 'rxjs';
import { FlightPathData } from '../data-models/flight-path-data.model';
import { parseGeoJsonFile as parseGeoJsonContent } from '../utils/geojson-utils';

/**
 * Mock coordinate data for generating realistic flight paths
 */
interface Coordinate {
  lat: number;
  lng: number;
}

/**
 * Flight map service for managing flight path data
 * Currently uses mock data - will connect to Hub API in future
 */
@Injectable({
  providedIn: 'root'
})
export class FlightMapService {

  /**
   * Generate a mock flight path based on a center point
   * Creates a realistic spray pattern (back-and-forth passes)
   */
  generateMockFlightPath(centerLat: number, centerLng: number, acreage: number): FlightPathData {
    // Calculate field dimensions based on acreage
    // 1 acre ≈ 4,047 m² ≈ 0.00000404 square degrees at mid-latitudes
    // For a rectangular field, assume width is 1.5x height
    const areaInDegrees = acreage * 0.0000015;
    const height = Math.sqrt(areaInDegrees / 1.5);
    const width = height * 1.5;

    // Calculate number of passes based on field size (more passes = denser pattern)
    const passes = Math.max(12, Math.min(30, Math.round(acreage / 2)));

    return {
      id: this.generateId(),
      name: `Flight Path - ${new Date().toLocaleDateString()}`,
      source: 'kml',
      fileName: 'uploaded_flight.kml',
      coordinates: this.generateComplexSprayPattern(
        { lat: centerLat, lng: centerLng },
        width,
        height,
        passes
      ),
      uploadedAt: new Date().toISOString(),
      areaAcres: acreage
    };
  }

  /**
   * Parse KML/KMZ file content and extract flight path data
   */
  parseKmlFile(file: File): Observable<FlightPathData> {
    return new Observable(observer => {
      const reader = new FileReader();

      reader.onload = (e) => {
        try {
          const xmlString = e.target?.result as string;
          const parser = new DOMParser();
          const xmlDoc = parser.parseFromString(xmlString, 'text/xml');

          // Check for parsing errors
          const parserError = xmlDoc.querySelector('parsererror');
          if (parserError) {
            throw new Error('Invalid XML/KML file');
          }

          // Extract coordinates from KML
          const coordinates = this.extractCoordinatesFromKml(xmlDoc);

          if (coordinates.length === 0) {
            throw new Error('No coordinates found in KML file');
          }

          // Calculate area from coordinates
          const areaAcres = this.calculateAreaFromCoordinates(coordinates);

          const flightPath: FlightPathData = {
            id: this.generateId(),
            name: file.name.replace(/\.(kml|kmz|gpx)$/i, ''),
            source: 'kml',
            fileName: file.name,
            coordinates: coordinates,
            uploadedAt: new Date().toISOString(),
            areaAcres: areaAcres
          };

          observer.next(flightPath);
          observer.complete();
        } catch (error) {
          console.error('KML parsing error:', error);
          observer.error(error);
        }
      };

      reader.onerror = () => {
        observer.error(new Error('Failed to read file'));
      };

      reader.readAsText(file);
    });
  }

  /**
   * Parse a GeoJSON file and extract coordinates as {lat, lng} array.
   * Supports Polygon, MultiPolygon, Feature, and FeatureCollection types.
   */
  parseGeoJsonFile(file: File): Observable<Coordinate[]> {
    return new Observable(observer => {
      const reader = new FileReader();

      reader.onload = (e) => {
        try {
          const content = e.target?.result as string;
          const coordinates = parseGeoJsonContent(content);

          if (coordinates.length === 0) {
            throw new Error('No polygon coordinates found in GeoJSON file');
          }

          observer.next(coordinates);
          observer.complete();
        } catch (error) {
          console.error('GeoJSON parsing error:', error);
          observer.error(error);
        }
      };

      reader.onerror = () => {
        observer.error(new Error('Failed to read file'));
      };

      reader.readAsText(file);
    });
  }

  /**
   * Extract coordinates from KML XML document.
   * Made public so boundary editor can reuse this for KML file import.
   */
  extractCoordinatesFromKml(xmlDoc: Document): Coordinate[] {
    const coordinates: Coordinate[] = [];

    // Try to find LineString or Polygon coordinates
    const coordinateElements = xmlDoc.getElementsByTagName('coordinates');

    for (let i = 0; i < coordinateElements.length; i++) {
      const coordText = coordinateElements[i].textContent?.trim();
      if (!coordText) continue;

      // KML coordinates are in format: longitude,latitude,altitude (space or newline separated)
      const coordPairs = coordText.split(/\s+/).filter(s => s.length > 0);

      for (const pair of coordPairs) {
        const parts = pair.split(',');
        if (parts.length >= 2) {
          const lng = parseFloat(parts[0]);
          const lat = parseFloat(parts[1]);

          if (!isNaN(lat) && !isNaN(lng)) {
            coordinates.push({ lat, lng });
          }
        }
      }
    }

    // Also try to extract from Placemark/Point elements
    if (coordinates.length === 0) {
      const pointElements = xmlDoc.getElementsByTagName('Point');
      for (let i = 0; i < pointElements.length; i++) {
        const coordElement = pointElements[i].getElementsByTagName('coordinates')[0];
        if (coordElement) {
          const coordText = coordElement.textContent?.trim();
          if (coordText) {
            const parts = coordText.split(',');
            if (parts.length >= 2) {
              const lng = parseFloat(parts[0]);
              const lat = parseFloat(parts[1]);
              if (!isNaN(lat) && !isNaN(lng)) {
                coordinates.push({ lat, lng });
              }
            }
          }
        }
      }
    }

    return coordinates;
  }

  /**
   * Calculate approximate area in acres from coordinates using Shoelace formula.
   * Made public so boundary editor can reuse this for real-time acreage calculation.
   */
  calculateAreaFromCoordinates(coordinates: Coordinate[]): number {
    if (coordinates.length < 3) {
      return 0;
    }

    // Convert to meters using rough approximation
    // 1 degree latitude ≈ 111,000 meters
    // 1 degree longitude ≈ 111,000 * cos(latitude) meters
    const avgLat = coordinates.reduce((sum, c) => sum + c.lat, 0) / coordinates.length;
    const latToMeters = 111000;
    const lngToMeters = 111000 * Math.cos(avgLat * Math.PI / 180);

    // Calculate area using Shoelace formula
    let area = 0;
    for (let i = 0; i < coordinates.length; i++) {
      const j = (i + 1) % coordinates.length;
      const xi = coordinates[i].lng * lngToMeters;
      const yi = coordinates[i].lat * latToMeters;
      const xj = coordinates[j].lng * lngToMeters;
      const yj = coordinates[j].lat * latToMeters;

      area += xi * yj - xj * yi;
    }

    area = Math.abs(area) / 2;

    // Convert square meters to acres (1 acre = 4046.86 m²)
    return area / 4046.86;
  }

  /**
   * Get sample flight paths for demo/testing
   * Uses realistic coordinates for agricultural fields in central Texas
   */
  getSampleFlightPaths(): FlightPathData[] {
    return [
      // Complex irregular field with dense spray pattern (like the example image)
      {
        id: 'sample-path-1',
        name: 'North Field - Morning Run',
        source: 'kml',
        fileName: 'north_field_am.kml',
        coordinates: this.generateComplexSprayPattern(
          // Center point: Agricultural area near Waco, TX
          { lat: 31.5493, lng: -97.1467 },
          0.008,  // Field width in degrees (~0.5 miles)
          0.006,  // Field height in degrees (~0.4 miles)
          24      // Number of spray passes
        ),
        uploadedAt: '2024-12-15T07:30:00Z',
        areaAcres: 45.2
      },
      // Second field nearby with different shape
      {
        id: 'sample-path-2',
        name: 'South Field - Afternoon',
        source: 'kml',
        fileName: 'south_field_pm.kml',
        coordinates: this.generateComplexSprayPattern(
          { lat: 31.5420, lng: -97.1520 },
          0.006,
          0.005,
          18
        ),
        uploadedAt: '2024-12-15T14:15:00Z',
        areaAcres: 38.5
      },
      // Smaller orchard section with tighter pattern
      {
        id: 'sample-path-3',
        name: 'West Orchard Section',
        source: 'gpx',
        fileName: 'west_orchard.gpx',
        coordinates: this.generateComplexSprayPattern(
          { lat: 31.5510, lng: -97.1580 },
          0.004,
          0.003,
          12
        ),
        uploadedAt: '2024-12-14T09:00:00Z',
        areaAcres: 22.8
      }
    ];
  }

  /**
   * Generate a complex spray pattern that mimics real drone flight paths
   * Creates a zigzag pattern with slight irregularities for realism
   */
  private generateComplexSprayPattern(
    center: Coordinate,
    width: number,
    height: number,
    passes: number
  ): Coordinate[] {
    const coordinates: Coordinate[] = [];
    const passWidth = width / passes;

    // Add slight randomness to make it look more natural
    const jitter = () => (Math.random() - 0.5) * 0.0001;

    // Starting point (southwest corner)
    const startLng = center.lng - width / 2;
    const startLat = center.lat - height / 2;
    const endLat = center.lat + height / 2;

    for (let i = 0; i < passes; i++) {
      const x = startLng + (i * passWidth);

      // Add intermediate points along each pass for smoother lines
      const pointsPerPass = 8;

      if (i % 2 === 0) {
        // Going north
        for (let j = 0; j <= pointsPerPass; j++) {
          const progress = j / pointsPerPass;
          const lat = startLat + (endLat - startLat) * progress;
          coordinates.push({
            lat: lat + jitter(),
            lng: x + jitter()
          });
        }
      } else {
        // Going south
        for (let j = pointsPerPass; j >= 0; j--) {
          const progress = j / pointsPerPass;
          const lat = startLat + (endLat - startLat) * progress;
          coordinates.push({
            lat: lat + jitter(),
            lng: x + jitter()
          });
        }
      }

      // Add turn point between passes
      if (i < passes - 1) {
        const turnLat = i % 2 === 0 ? endLat : startLat;
        const nextX = startLng + ((i + 1) * passWidth);
        coordinates.push({
          lat: turnLat + jitter(),
          lng: (x + nextX) / 2 + jitter()
        });
      }
    }

    return coordinates;
  }

  /**
   * Calculate the bounding box for a set of coordinates
   */
  calculateBounds(coordinates: Coordinate[]): {
    north: number;
    south: number;
    east: number;
    west: number;
    center: Coordinate;
  } {
    if (coordinates.length === 0) {
      return {
        north: 0,
        south: 0,
        east: 0,
        west: 0,
        center: { lat: 0, lng: 0 }
      };
    }

    let north = coordinates[0].lat;
    let south = coordinates[0].lat;
    let east = coordinates[0].lng;
    let west = coordinates[0].lng;

    coordinates.forEach(coord => {
      north = Math.max(north, coord.lat);
      south = Math.min(south, coord.lat);
      east = Math.max(east, coord.lng);
      west = Math.min(west, coord.lng);
    });

    return {
      north,
      south,
      east,
      west,
      center: {
        lat: (north + south) / 2,
        lng: (east + west) / 2
      }
    };
  }

  /**
   * Generate unique ID for flight paths
   */
  private generateId(): string {
    return `path_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }
}
